import {
  useState,
  useContext,
  type Dispatch,
  type SetStateAction,
  type ReactNode,
  lazy,
  useEffect,
} from 'react';
import { useOktaAuth } from '@okta/okta-react/';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import SWLogo from '../../assets/sw-logo-top-nav.svg';
import { UserContext } from '../../user/UserContext';
import type { UserContextType } from '../../@types/user';
import { useLocationStore } from '../../stores/locationStore';
import {
  mediaQueries,
  themeColors,
  themeSizes,
} from '../../styles/globalStyleVars';
import { callEvent } from '../../utils/Amplitude/AmplitudeUtilities';
import PreventNavigationModal from '../Global/NavigationPrompt/PreventNavigationModal';
import CalendarLogo from '../../assets/calendar.svg';
import IntroductionLogo from '../../assets/introduction.svg';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useGetUserEventInfo } from '../../utils/api/requests/useQueries';
import {
  IconAlertTriangleFilled,
  IconLogout,
  IconMenu2,
  IconSearch,
  IconSettings,
  IconX,
} from '@tabler/icons-react';
import { useSignout } from '../../user/useSignout';
import { AppPlatforms } from '../../API';
import { UserEventTypes } from '../../interfaces/UserEventInterface';
import OnboardingModal from '../Global/Modals/OnboardingModal/OnboardingModal';
import IntroductionModal from '../Global/Modals/IntroductionModal/IntroductionModal';
import { TypographyStyled } from '../Styled/Typography';
import {
  Avatar,
  Badge,
  Button as CoreButton,
  Divider,
  Drawer,
  IconButton,
  Menu,
} from '@cfa/react-core';
import { getInitials } from '../../utils/strings/formatters';
import useBreakpoints from '../../hooks/useBreakpoints';
import useMediaQuery from '../../hooks/useMediaQuery';

const LogoutModal = lazy(
  () => import('../Global/Modals/LogoutModal/LogoutModal')
);

const StyledTopNavigation = styled.header`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 9px 16px;

  ${mediaQueries.lg_up} {
    padding: 9px 48px;
  }
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1200;
  background-color: ${themeColors.primaryWhite};
  box-shadow: 0 2px 4px rgba(91, 103, 112, 0.24);

  a,
  button {
    outline: none;

    &:focus-visible {
      outline: 2px solid ${themeColors.focus};
      outline-offset: 2px;
    }
  }

  button[data-testid='nav-avatar-btn'] {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
  }

  button[data-testid='nav-avatar-btn']:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${themeColors.focus};
    border-radius: 50%;
  }
`;

const NavLogoLink = styled(Link)`
  line-height: 1;
  margin-right: ${themeSizes.sm};
  img {
    width: ${themeSizes.xxl};
    height: auto;
  }
`;

const NavDrawerTray = styled(Drawer.Tray)`
  overflow-y: hidden;
  z-index: 3000;
`;

const NavDrawerCloseWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 74px;
  padding: ${themeSizes.xs} 0px;

  button {
    width: auto;
    align-self: flex-end;
  }

  svg {
    height: 18px;
    width: 18px;
  }
`;

const NavDrawerContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: space-between;
`;

const NavDrawerContentSection = styled.div`
  display: flex;
  flex-direction: column;

  &:last-child {
    padding: ${themeSizes.md} ${themeSizes.smMd} ${themeSizes.xl};
  }
`;

const NameText = styled.span`
  font-weight: 700;
  color: ${themeColors.colorGray_6};
  &.mobileUsername {
    position: relative;
    top: -10px;
  }
`;

const DrawerButton = styled(CoreButton)`
  display: flex;
  width: 100%;
  justify-content: flex-start;
  padding: ${themeSizes.smMd} ${themeSizes.xs};
  letter-spacing: 0;
  font-weight: 500;
`;

const AlertDrawerNavButton = styled(IconButton)<{ $apiError: boolean }>`
  color: ${themeColors.secondaryBlue} !important;
  margin-top: 6px;
  margin-right: ${themeSizes.smMd};

  ${mediaQueries.sm_up} {
    margin-right: ${themeSizes.lg};
  }

  > svg {
    ${({ $apiError }) =>
      $apiError && `color: ${themeColors.errorColor} !important`};
  }
`;

const IntroductionButtonWrapper = styled.span`
  display: flex;
  align-items: center;
  border: 1px solid ${themeColors.colorGray_1};
  border-radius: ${themeSizes.sm};
  padding: 10px;
`;

const NavAvatarButton = styled(CoreButton)`
  background: none !important;
  border: none !important;
  min-width: 0 !important;
  min-height: 0 !important;
  padding: 0 !important;
  cursor: pointer;

  &:focus-visible {
    outline: none !important;
    box-shadow: 0 0 0 2px ${themeColors.focus};
    border-radius: 50%;
  }
`;

const NavSection = styled.div<{
  $grow?: boolean;
  $end?: boolean;
  $hidden?: boolean;
}>`
  display: flex;
  align-items: center;
  ${({ $grow }) => $grow && 'flex: 1;'}
  ${({ $end }) => $end && 'justify-content: flex-end; margin-left: auto;'}
  ${({ $hidden }) => $hidden && 'display: none;'}
`;

const TopNavLink = styled(Link)<{ $isActive?: boolean }>`
  background: none;
  border: none;
  color: ${({ $isActive }) =>
    $isActive ? themeColors.primaryRed : themeColors.colorGray_6};
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1;
  position: relative;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    color: ${themeColors.primaryRed};
    text-decoration: none;
  }

  ${({ $isActive }) =>
    $isActive &&
    `
    &::after {
      border-radius: 4px;
      border: 2px solid ${themeColors.primaryRed};
      bottom: -26px;
      content: '';
      display: block;
      left: 0;
      position: absolute;
      width: 100%;
    }
  `}
`;

const DrawerNavLink = styled(Link)<{ $isActive?: boolean }>`
  background-color: ${({ $isActive }) =>
    $isActive ? themeColors.selectOptionHover : 'transparent'};
  border-left: 3px solid
    ${({ $isActive }) => ($isActive ? themeColors.primaryRed : 'transparent')};
  color: ${({ $isActive }) =>
    $isActive ? themeColors.primaryRed : themeColors.colorGray_8};
  display: block;
  font-size: 1rem;
  font-weight: ${({ $isActive }) => ($isActive ? '700' : '400')};
  padding: 14px 16px;
  text-decoration: none;

  &:hover {
    color: ${themeColors.primaryRed};
    text-decoration: none;
  }
`;

const TopNavButton = styled.button`
  background: none;
  border: none;
  color: ${themeColors.colorGray_6};
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1;
  padding: 0;
  position: relative;
  text-decoration: none;
  white-space: nowrap;
`;

type NavigationProps = {
  alertsDrawerOpen: boolean;
  setAlertsDrawerOpen: Dispatch<SetStateAction<boolean>>;
  alertsTotal: number;
  alertsError: boolean;
};

type AlertButtonProps = {
  children: ReactNode;
};

const Navigation = ({
  alertsDrawerOpen,
  setAlertsDrawerOpen,
  alertsTotal,
  alertsError,
}: NavigationProps) => {
  const {
    userState,
    isFormDirty,
    setLogoutClicked,
    logoutClicked,
    setIsOnboarded,
    isOnboarded,
    userAvatarPath,
    isSessionTimeout,
    setShowLogoutModal,
    showLogoutModal,
    setLogUserOut,
    logUserOut,
  } = useContext(UserContext) as UserContextType;
  const { activeLocation } = useLocationStore();
  const { handleLogout } = useSignout();
  const { oktaAuth } = useOktaAuth();
  const [open, setOpen] = useState<boolean>(false);
  const breakpoints = useBreakpoints();
  const isBelowLg = useMediaQuery(breakpoints.down('lg'));
  const isBelowMd = useMediaQuery(breakpoints.down('md'));
  const isBelowSm = useMediaQuery(breakpoints.down('sm'));
  const [showIntroductionModal, setShowIntroductionModal] =
    useState<boolean>(false);
  //biome-ignore-start lint/complexity/useLiteralKeys: LD flag variable renaming
  const { ['equipment-tab']: equipmentTab } = useFlags();
  const { ['whats-new']: whatsNew } = useFlags();
  //biome-ignore-end lint/complexity/useLiteralKeys: LD flag variable renaming
  const navigate = useNavigate();
  const locationPath = activeLocation ? `/${activeLocation}` : '';

  const { data: userOnboarding, isSuccess: userOnboardingSuccess } =
    useGetUserEventInfo(UserEventTypes.COMPLETE_ONBOARDING, {
      platforms: [AppPlatforms.WEB],
    });

  const { data: userWhatsNew, isSuccess: userWhatsNewSuccess } =
    useGetUserEventInfo(UserEventTypes.VIEW_WHATS_NEW, {
      platforms: [AppPlatforms.WEB],
    });

  //biome-ignore lint/correctness/useExhaustiveDependencies: limited effect dependencies
  useEffect(() => {
    if (!userOnboardingSuccess || !userWhatsNewSuccess) return;
    const hasOnboarded = userOnboarding?.count > 0;
    const hasSeenWhatsNew = userWhatsNew?.count > 0;
    setIsOnboarded(hasOnboarded);
    // Show a modal if the user hasn't onboarded yet, or has onboarded but hasn't seen What's New
    setShowIntroductionModal(!hasOnboarded || !hasSeenWhatsNew);
  }, [userOnboardingSuccess, userWhatsNewSuccess]);

  //biome-ignore lint/correctness/useExhaustiveDependencies: limited effect dependencies
  useEffect(() => {
    if (!logUserOut && !isSessionTimeout) return;
    if (isSessionTimeout) {
      handleLogout();
      return;
    }
    handleLogout(1500);
  }, [logUserOut, isSessionTimeout]);

  const closeDrawer = () => {
    setOpen(false);
  };

  const login = async () => {
    callEvent('clicked navlink', {
      locationNum: activeLocation,
      linkName: 'Login',
    });

    oktaAuth.signInWithRedirect({ originalUri: '/' });
  };

  //logs mobile events
  const handleMobileNavClick = async (clickedLink: string) => {
    callEvent('clicked mobile navlink', {
      locationNum: activeLocation,
      linkName: clickedLink,
    });

    closeDrawer();
  };

  const handleAlertsIconClick = () => {
    setAlertsDrawerOpen(!alertsDrawerOpen);
    callEvent('view alerts', {});
  };

  //when logout is clicked it will launch logout modal which will display for 1.5 seconds and then log user out
  const logout = async () => {
    isFormDirty ? setShowLogoutModal(false) : setShowLogoutModal(true);
    localStorage.removeItem('location-storage');

    callEvent('clicked navlink', {
      locationNum: activeLocation,
      linkName: 'Logout',
    });

    isFormDirty && setLogoutClicked(true);
  };

  // Add navigation links here
  const navLinks = [
    { route: '/', linkText: 'Home' },
    { route: `/tickets${locationPath}`, linkText: 'Tickets' },
    { route: '/forms', linkText: 'Open a ticket' },
    // Feature Flag - equipmentTab
    ...(equipmentTab
      ? [
          {
            route: `/equipment${locationPath}`,
            linkText: 'Equipment',
          },
        ]
      : []),
  ];

  const AlertsIconButton = ({ children }: AlertButtonProps) => {
    return (
      <AlertDrawerNavButton
        aria-label='Alerts'
        onPress={() => handleAlertsIconClick()}
        $apiError={alertsError}
        data-testid='alerts-outages-btn'
      >
        {alertsTotal > 0 ? (
          <Badge badgeContent={alertsTotal} data-testid='badge-content'>
            {children}
          </Badge>
        ) : (
          //biome-ignore lint/complexity/noUselessFragments: expects element return type
          <>{children}</>
        )}
      </AlertDrawerNavButton>
    );
  };

  const showOnboardingModal =
    showIntroductionModal && (!whatsNew || !isOnboarded);
  const showIntroModal = showIntroductionModal && whatsNew && isOnboarded;

  return (
    <>
      <StyledTopNavigation>
        <NavSection>
          <NavLogoLink
            to='/'
            onClick={() =>
              callEvent('clicked navlink', {
                locationNum: activeLocation,
                linkName: 'Nav Logo',
              })
            }
          >
            <img src={SWLogo} alt='Chick-fil-A Support Circle Logo' />
          </NavLogoLink>
        </NavSection>
        <NavSection $grow $hidden={isBelowMd}>
          {navLinks.map(({ route, linkText }) => {
            const isActive =
              route === '/'
                ? window.location.pathname === '/' ||
                  window.location.pathname === '/home'
                : window.location.pathname.startsWith(route);
            return (
              <TopNavLink
                key={linkText}
                to={route}
                $isActive={isActive}
                aria-current={isActive ? 'page' : undefined}
                data-testid={`nav_${linkText
                  .replace(/\s/g, '-')
                  .toLowerCase()}`}
                onClick={() =>
                  callEvent('clicked navlink', {
                    locationNum: activeLocation,
                    linkName: linkText,
                  })
                }
                style={
                  isBelowLg
                    ? { margin: '0 10px' }
                    : { margin: `0 ${themeSizes.md}` }
                }
              >
                {linkText}
              </TopNavLink>
            );
          })}
        </NavSection>
        <NavSection $end>
          {whatsNew && (
            <TopNavButton
              onClick={() => {
                setShowIntroductionModal(true);
                callEvent(
                  whatsNew ? 'view whats new modal' : 'view onboarding modal',
                  {
                    locationNum: activeLocation,
                    path: window.location.href,
                  }
                );
              }}
              data-testid='nav_whats-new'
              style={{ marginTop: '2px', marginRight: themeSizes.sm }}
            >
              <IntroductionButtonWrapper>
                {!isBelowLg && (
                  <TypographyStyled
                    color={themeColors.secondaryBlue}
                    fontWeight='medium'
                    style={{ marginRight: '10px' }}
                  >
                    See what&apos;s new
                  </TypographyStyled>
                )}
                <img src={IntroductionLogo} alt='Introduction Logo' />
              </IntroductionButtonWrapper>
            </TopNavButton>
          )}
          <TopNavLink
            // Do not forward page-specific params (for example ticketQuery) into /search.
            // Carrying those params created extra URL normalization and back-button loops.
            to='/search'
            onClick={() =>
              callEvent('clicked search icon', {
                locationNum: activeLocation,
              })
            }
            data-testid='nav_search'
            style={{
              marginTop: '6px',
              marginRight: themeSizes.lg,
              marginLeft: themeSizes.md,
            }}
          >
            <IconSearch size={28} color={themeColors.secondaryBlue} />
          </TopNavLink>
          <TopNavLink
            to='/appointments'
            onClick={() =>
              callEvent('view all appointments', {
                locationNum: activeLocation,
              })
            }
            data-testid='nav_appointments'
            style={{ marginTop: '5px', marginRight: '20px' }}
          >
            <img src={CalendarLogo} alt='Calendar Logo' />
          </TopNavLink>
          <AlertsIconButton>
            <IconAlertTriangleFilled size={27} />
          </AlertsIconButton>
          {!isBelowSm && (
            <Menu.Root>
              <NavAvatarButton
                onPress={() =>
                  callEvent('clicked navlink', {
                    locationNum: activeLocation,
                    linkName: 'Profile Avatar',
                  })
                }
                data-testid='nav-avatar-btn'
                style={{
                  marginBottom: themeSizes.xs,
                  marginRight: isBelowMd ? themeSizes.md : '0px',
                }}
              >
                <Avatar.Root>
                  {userAvatarPath && (
                    <Avatar.Image src={userAvatarPath} alt='Profile Avatar' />
                  )}
                  <Avatar.Fallback>
                    {getInitials(
                      `${userState.userDetails?.display_name} ${userState.userDetails?.display_last_name}`
                    )}
                  </Avatar.Fallback>
                </Avatar.Root>
              </NavAvatarButton>
              <Menu.Popover placement='bottom end' className='popper-menu'>
                <div data-testid='nav-user-menu'>
                  {userState.isLoggedIn ? (
                    <>
                      <div
                        style={{
                          padding: themeSizes.smMd,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Avatar.Root style={{ marginRight: themeSizes.smMd }}>
                          {userAvatarPath && (
                            <Avatar.Image
                              src={userAvatarPath}
                              alt='Profile Avatar'
                            />
                          )}
                          <Avatar.Fallback>
                            {getInitials(
                              `${userState.userDetails?.display_name} ${userState.userDetails?.display_last_name}`
                            )}
                          </Avatar.Fallback>
                        </Avatar.Root>
                        <NameText data-testid='nav-user-name'>{`${userState.userDetails?.display_name} ${userState.userDetails?.display_last_name}`}</NameText>
                      </div>
                      <Divider style={{ marginInline: themeSizes.md }} />
                      <Menu.Container>
                        <Menu.Item
                          id='settings'
                          style={{
                            paddingBottom: '6px',
                            color: themeColors.secondaryBlue,
                            fontWeight: 500,
                          }}
                          onAction={() => {
                            navigate('/settings');
                            callEvent('clicked navlink', {
                              locationNum: activeLocation,
                              linkName: 'Settings',
                            });
                          }}
                        >
                          <IconSettings />
                          Settings
                        </Menu.Item>
                        <Menu.Item
                          id='signout'
                          style={{
                            paddingTop: '6px',
                            color: themeColors.secondaryBlue,
                            fontWeight: 500,
                          }}
                          onAction={logout}
                        >
                          <IconLogout />
                          Log out
                        </Menu.Item>
                      </Menu.Container>
                    </>
                  ) : (
                    <Menu.Container>
                      <Menu.Item id='signin' onAction={login}>
                        Log In
                      </Menu.Item>
                    </Menu.Container>
                  )}
                </div>
              </Menu.Popover>
            </Menu.Root>
          )}
          {isBelowMd && (
            <IconButton
              aria-label={`${open ? 'close' : 'open'} menu`}
              className='collapse-menu'
              size='sm'
              onPress={() => setOpen(prevState => !prevState)}
            >
              {open ? (
                <IconX color={themeColors.colorGray_6} />
              ) : (
                <IconMenu2 color={themeColors.colorGray_6} />
              )}
            </IconButton>
          )}
        </NavSection>
      </StyledTopNavigation>
      <div style={{ minHeight: '74px' }} />
      <Drawer.Root
        isOpen={isBelowMd && open}
        onOpenChange={isOpen => {
          if (!isOpen) closeDrawer();
        }}
      >
        <Drawer.Overlay>
          <NavDrawerTray position='right' isCloseButtonHidden>
            <NavDrawerCloseWrapper>
              <IconButton
                aria-label='Close menu'
                color='secondary'
                onPress={() => closeDrawer()}
              >
                <IconX
                  color={themeColors.colorGray_6}
                  style={{ height: '26px', width: '26px' }}
                />
              </IconButton>
            </NavDrawerCloseWrapper>
            <NavDrawerContentWrapper>
              <NavDrawerContentSection>
                {navLinks.map(({ route, linkText }) => (
                  <DrawerNavLink
                    key={linkText}
                    to={route}
                    $isActive={
                      route === '/'
                        ? window.location.pathname === '/' ||
                          window.location.pathname === '/home'
                        : window.location.pathname.startsWith(route)
                    }
                    onClick={() => handleMobileNavClick(linkText)}
                  >
                    {linkText}
                  </DrawerNavLink>
                ))}
              </NavDrawerContentSection>
              <NavDrawerContentSection>
                {isBelowSm && (
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: themeSizes.smMd,
                      }}
                    >
                      <Avatar.Root style={{ marginRight: themeSizes.smMd }}>
                        {userAvatarPath && (
                          <Avatar.Image
                            src={userAvatarPath}
                            alt='Profile Avatar'
                          />
                        )}
                        <Avatar.Fallback>
                          {getInitials(
                            `${userState.userDetails?.display_name} ${userState.userDetails?.display_last_name}`
                          )}
                        </Avatar.Fallback>
                      </Avatar.Root>
                      {userState.isLoggedIn && (
                        <NameText>
                          {`${userState.userDetails?.display_name} ${userState.userDetails?.display_last_name}`}
                        </NameText>
                      )}
                    </div>
                    {userState.isLoggedIn ? (
                      <>
                        <Divider
                          style={{
                            margin: `10px ${themeSizes.md} ${themeSizes.xs}`,
                          }}
                        />
                        <DrawerButton
                          color='secondary'
                          onPress={() => {
                            navigate('/settings');
                            callEvent('clicked navlink', {
                              locationNum: activeLocation,
                              linkName: 'Settings (mobile)',
                            });
                            closeDrawer();
                          }}
                          variant='text'
                        >
                          <IconSettings
                            style={{ marginRight: themeSizes.smMd }}
                          />
                          Settings
                        </DrawerButton>
                        <DrawerButton
                          color='secondary'
                          onPress={logout}
                          variant='text'
                        >
                          <IconLogout
                            style={{ marginRight: themeSizes.smMd }}
                          />
                          Log out
                        </DrawerButton>
                      </>
                    ) : (
                      <DrawerButton
                        color='secondary'
                        onPress={login}
                        variant='text'
                      >
                        Log In
                      </DrawerButton>
                    )}
                  </div>
                )}
              </NavDrawerContentSection>
            </NavDrawerContentWrapper>
          </NavDrawerTray>
        </Drawer.Overlay>
      </Drawer.Root>

      <OnboardingModal
        hasEquipmentPermission={equipmentTab}
        isOnboarded={isOnboarded}
        onClose={() => setShowIntroductionModal(false)}
        setIsOnboarded={setIsOnboarded}
        show={showOnboardingModal}
        showWhatsNewFeatureFlag={whatsNew}
        showOnboardingModal={(show: boolean) => setShowIntroductionModal(show)}
      />

      <IntroductionModal
        isOnboarded={isOnboarded}
        onClose={() => setShowIntroductionModal(false)}
        setIsOnboarded={setIsOnboarded}
        show={showIntroModal}
        showWhatsNewFeatureFlag={whatsNew}
      />

      <PreventNavigationModal
        onClose={() => setLogoutClicked(false)}
        onCancel={() => setLogoutClicked(false)}
        onConfirm={() => setShowLogoutModal(true)}
        show={logoutClicked && !showLogoutModal}
        headerText='Discard Ticket?'
        bodyText='Leaving will discard all progress you’ve made toward opening your
        ticket. Are you sure you want to continue?'
        confirmButtonText='Discard Ticket'
      />

      <LogoutModal
        showLogoutModal={showLogoutModal}
        setShowLogoutModal={setShowLogoutModal}
        logUserOut={logUserOut}
        setLogUserOut={setLogUserOut}
        setLogoutClicked={setLogoutClicked}
        userName={userState?.userDetails?.['cfa-login']}
      />
    </>
  );
};

export default Navigation;
